import { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { sortBy, isUndefined, isEmpty, find, findIndex } from "lodash"

import debug from "@plannel/utils/debug-util";
import Snackbar from "@plannel/components/Snackbar";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import MeasureConfigList from "@plannel/components/modal/MeasureConfigList";
import ColorUtil from "@plannel/pages/utils/color-util";
import p13nService from "@plannel/services/system/p13n-service";

/*
 * 공용 차트 설정(rechart 계열) — ChartComposed / PivotChartComposed / FillRateDashboard.
 * 셸 = BaseConfigModal, 본문 = MeasureConfigList (표준 — 규약: saas-web/.claude/skills/config-modal/SKILL.md).
 *
 * 저장 포맷(기존 유지): configObj.chart[chartId] = { [id|name]: { activeFlg, text, type } }
 *   · `type` 이 **차트 타입**(line/bar/area/dashed)이다. z_p13n pageCd = viewName.
 *   · multiSeries=false 면 활성 측정치는 1개만 허용.
 */
// 차트 타입 4종 — 앱 전역 규약(DW 대시보드 DW_CHART_TYPES 와 같은 목록, 여긴 소문자).
//   dashed = 점선 라인. 렌더는 ChartComposed / PivotChartComposed 가 담당한다.
//   ⚠️ 누적(stackId) 차트에서는 line 과 마찬가지로 쌓이지 않고 선으로 빠진다(의도된 동작).
const CHART_TYPES = ["line", "bar", "area", "dashed"];

// 행 식별자 — id 가 없으면 name 으로(기존 동작 유지)
const rowKey = (r) => (isUndefined(r.id) ? r.name : r.id);

const ChartCommonConfigModal = (props) => {
  const { t, open, setOpen, viewName, dataSeries, colorOrderSeries, modalResultData, chartId, multiSeries = true } = props;
  const [p13nConfigObj, setP13nConfigObj] = useState({});
  const [measureData, setMeasureData] = useState([]);   // [{ id, name, type, activeFlg }]
  const [draft, setDraft] = useState({});                // { [rowKey]: { activeFlg, type } }
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const { snackBarMessageColor } = ColorUtil;

  /*
   * 목록·색의 **단일 기준 순서** = 페이지가 선언한 원본 순서(colorOrderSeries, 없으면 dataSeries).
   * 차트가 색 인덱스를 뽑는 기준과 같아야 목록 순서와 스와치 색이 늘 함께 간다
   * (ChartComposed / PivotChartComposed 의 colorIndexOf 와 같은 규칙).
   */
  const orderIndexOf = (id) => {
    const orderSeries = isEmpty(colorOrderSeries) ? dataSeries : colorOrderSeries;
    return findIndex(orderSeries, (s) => s.field === id || s.text === id);
  };

  /*
   * 행 순서 고정 — 예전엔 차트 타입(type)으로 정렬했다. 그러면 **타입을 바꿔 저장하고 다시 열 때마다
   * 목록 순서가 바뀐다**: 저장 포맷(configObj.chart[chartId])이 행 순서를 그대로 굳히고,
   * 다음 열람은 그 키 순서로 목록을 만들기 때문에 한 번 어긋난 순서가 계속 따라온다.
   * 기준 목록에 없는 행은 lodash sortBy 가 안정 정렬이라 원래 상대 순서를 유지한 채 뒤로 간다.
   */
  const orderRows = (rows) => sortBy(rows, [(r) => {
    const i = orderIndexOf(rowKey(r));
    return i >= 0 ? i : Number.MAX_SAFE_INTEGER;
  }]);

  // 행 목록이 정해지면 편집본(draft) 동기화
  const applyRows = (rows) => {
    setMeasureData(rows);
    const next = {};
    rows.forEach((r) => { next[rowKey(r)] = { activeFlg: r.activeFlg, type: r.type }; });
    setDraft(next);
  };

  // p13n 설정이 없을 때 — dataSeries 그대로(기본 활성)
  const setDefaultRows = (series) => {
    applyRows(orderRows(series.map((target) => ({
      id: target.field,
      activeFlg: target.activeFlg !== undefined ? target.activeFlg : true,
      name: target.text,
      type: target.type,
    }))));
  };

  const retrieveMeasures = () => {
    p13nService.get(viewName)
      .then(response => {
        const { data } = response;
        setP13nConfigObj(data);

        if (data?.chart && data?.chart?.[chartId]) {
          const datas = [];
          const entrySet = Object.entries(data.chart[chartId]);

          for (const [key, value] of entrySet) {
            const checkedMeasure = dataSeries.filter(measure => measure.text === key);
            const filteredData = multiSeries ? dataSeries.filter(series => series.field === key) : dataSeries.filter(series => series.text === key);

            if (multiSeries) {
              if (filteredData.length > 0) {
                datas.push({ id: key, activeFlg: value.activeFlg, name: filteredData[0].text, type: value.type });
              }
            } else {
              if (filteredData.length > 0 && checkedMeasure) {
                datas.push({ id: key, activeFlg: value.activeFlg, name: filteredData[0].text, type: value.type });
              }
            }
          }

          if (!multiSeries) { // for case :: MonthlyBySku
            dataSeries.forEach(element => {
              const p13nMeasure = datas.filter(measure => measure.name === element.text);
              if (p13nMeasure.length === 0) {
                datas.push({ id: element.field, activeFlg: element.activeFlg, name: element.text, type: element.type });
              }
            });
          }

          applyRows(orderRows(datas));
        } else {
          setDefaultRows(dataSeries);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  useEffect(() => {
    if (open) {
      retrieveMeasures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /*
   * 측정치별 색 — 차트(ChartComposed / PivotChartComposed)와 **같은 인덱스 규칙**으로 계산해 스와치로 보여준다.
   * 차트의 색 인덱스는 페이지가 선언한 원본 순서(colorOrderSeries)에서 나온다 — 차트타입·활성 여부와 무관하다.
   *   ⚠ 여기서 draft.type 으로 다시 정렬하면(예전 코드) 타입을 바꿀 때마다 스와치 색이 흔들린다.
   * colorOrderSeries 가 없으면 행 순서로 폴백 — 차트 쪽 폴백(fallbackIndex)과 같은 성격이다.
   */
  /*
   * 페이지가 시리즈에 색을 지정했으면(typeOptions.fill/stroke) 차트는 그 색으로 그린다 → 스와치도 그 색.
   *   어느 쪽이 이기는지는 차트 타입마다 다르다(ChartComposed / PivotChartComposed 렌더 분기와 같은 규칙):
   *   bar·area 는 stroke 우선, line·dashed 는 fill 우선.
   */
  const assignedColorOf = (key, type) => {
    const orderSeries = isEmpty(colorOrderSeries) ? dataSeries : colorOrderSeries;
    const series = find(orderSeries, (s) => s.field === key || s.text === key)
      ?? find(dataSeries, (s) => s.field === key || s.text === key);
    const to = series?.typeOptions;
    if (!to) {
      return undefined;
    }
    return type === "bar" || type === "area" ? (to.stroke || to.fill) : (to.fill || to.stroke);
  };

  const colorByKey = useMemo(() => {
    const map = {};
    measureData.forEach((r, idx) => {
      const key = rowKey(r);
      const i = orderIndexOf(key);
      map[key] = assignedColorOf(key, (draft[key]?.type ?? r.type)?.toLowerCase())
        || ColorUtil.colorByMeasure(r.id, i >= 0 ? i : idx);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureData, draft, colorOrderSeries, dataSeries]);

  const listMeasures = useMemo(
    () => measureData.map((r) => ({
      key: rowKey(r),
      label: GridUtils.gridValueL10N(r.name),
      hasActive: true,
      color: colorByKey[rowKey(r)],
    })),
    [measureData, colorByKey],
  );

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = () => {
    const results = {};
    const content = { ...p13nConfigObj };
    const activeMeasure = [];

    measureData.forEach((r) => {
      const key = rowKey(r);
      const d = draft[key] ?? {};
      results[key] = { activeFlg: d.activeFlg, text: r.name, type: d.type };
      if (d.activeFlg) {
        activeMeasure.push(r.name);
      }
    });

    if (!multiSeries) {
      if (activeMeasure.length > 1) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_SelectOneMeasure") });
        return;
      }
    }

    if (Object.keys(content).length !== 0 && content.chart) {
      content.chart[chartId] = {};
    } else {
      content.chart = {};
    }

    content.chart[chartId] = results;

    const params = {
      pageCd: viewName,
      configObj: content
    };

    p13nService.upsert(params)
      .then((response) => {
        if (response.status === 200) {
          modalResultData({ content, reload: true })
        }
      })
      .catch((e) => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .finally(() => {
        setOpen(false);
      });
  };

  return (
    <>
      <BaseConfigModal
        open={open}
        onClose={handleClose}
        title={t("chartConfig")}
        onSave={handleConfirm}
      >
        <MeasureConfigList
          open={open}
          measures={listMeasures}
          draft={draft}
          setDraft={setDraft}
          chartTypes={CHART_TYPES}
          typeField="type"
        />
      </BaseConfigModal>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  )
}

export default withTranslation()(ChartCommonConfigModal);
